<script lang="ts">
// Packages
import { defineComponent, type PropType } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisClearRefinements,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisCurrentRefinements,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';

type RefinementList = { [key: string]: string[] };

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisClearRefinements,
    AisHits,
    AisInstantSearch,
    AisPagination,
    AisCurrentRefinements,
    RefinementPanel,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
    heading: {
      type: String,
      required: false,
    },
    headingElement: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      searchClient: algoliasearch(this.appId, this.searchApiKey),
    };
  },
  computed: {
    routing() {
      const { indexName, refinements } = this as { indexName: string; refinements: Refinement[] };
      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page && page > 1) {
              route.p = page.toString();
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            return {
              [indexName]: {
                query: routeState.q,
                refinementList: Object.fromEntries(
                  refinements
                    .map(({ attribute }) => [
                      attribute,
                      routeState[attribute] ? routeState[attribute].split('~') : undefined,
                    ])
                    .filter(([, val]) => val !== undefined),
                ),
                page: Number(routeState.p),
              },
            };
          },
        },
      };
    },
  },
  methods: {
    onPageChange() {
      this.$el.scrollIntoView();
    },
  },
});
</script>
<template>
  <ais-instant-search :search-client="searchClient" :routing="routing" :index-name="indexName">
    <!-- ====== START REFINEMENTS ====== -->
    <div class="mb-8 bg-harwood-surface text-harwood-offblack pb-8 pt-5 sm:mb-5 lg:pb-12 lg:pt-10">
      <div class="container">
        <h3 class="mb-6 text-center text-3.5xl font-bold">Filter by</h3>
        <div
          class="mb-6 grid grid-cols-[minmax(12.5rem,max-content)] justify-center gap-x-8 gap-y-5 lg:mb-11 lg:auto-cols-[minmax(12.5rem,max-content)] lg:grid-flow-col lg:items-center"
        >
          <refinement-panel
            v-for="refinement in refinements"
            :key="refinement.attribute"
            v-bind="refinement"
          />
        </div>

        <!-- ====== START CURRENT REFINEMENTS ====== -->
        <div class="sm:relative sm:flex sm:min-h-11 sm:justify-center">
          <!-- Use mx to prevent overflow with reset button -->
          <ais-current-refinements
            class="mb-8 sm:mx-28 sm:mb-0 z-10"
            :class-names="{
              'ais-CurrentRefinements--noRefinement': 'hidden',
            }"
          >
            <template #default="{ items /* , createURL */ }">
              <ul class="flex flex-wrap justify-center gap-6">
                <template v-for="item in items" :key="item.attribute">
                  <li
                    v-for="refinement in item.refinements"
                    :key="
                      [
                        refinement.attribute,
                        refinement.type,
                        refinement.value,
                        refinement.operator,
                      ].join(':')
                    "
                    class="flex items-center rounded-full bg-harwood-blue-300 text-white font-medium font-montserrat px-8 py-1.5 text-base"
                  >
                    {{ refinement.label }}
                    <button class="ml-3" @click.prevent="item.refine(refinement)">
                      <svg
                        class="text-white"
                        height="14"
                        width="14"
                        viewBox="0 0 13 13"
                        role="img"
                        aria-label="Remove refinement"
                      >
                        <line
                          x1="0.6"
                          y1="0.6"
                          x2="12.4"
                          y2="12.4"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <line
                          x1="0.6"
                          y1="12.4"
                          x2="12.4"
                          y2="0.6"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </li>
                </template>
              </ul>
            </template>
          </ais-current-refinements>
          <ais-clear-refinements
            class="h-full text-center text-base font-bold text-harwood-offblack sm:absolute sm:right-0 sm:flex sm:text-start"
            :class-names="{
              'ais-ClearRefinements-button--disabled': 'hidden',
            }"
          >
            <template #resetLabel>Clear filters</template>
          </ais-clear-refinements>
        </div>
        <!-- ====== END CURRENT REFINEMENTS ====== -->
      </div>
    </div>
    <!-- ====== END REFINEMENTS ====== -->
    <div v-if="heading">
      <h2 class="text-center text-3.5xl font-bold">{{ heading }}</h2>
    </div>
    <!-- ====== START HITS ====== -->
    <ais-hits
      :class-names="{
        'ais-Hits': 'container max-w-slim my-[3.75rem]',
        'ais-Hits-list':
          'grid gap-8 md:gap-20 grid-cols-[repeat(auto-fit,16.5rem)] justify-center overflow-hidden',
        'ais-Hits-item': 'mx-auto',
      }"
    >
      <template #item="props">
        <slot name="item" v-bind="props" />
      </template>
    </ais-hits>
    <!-- ====== END HITS ====== -->

    <!-- ====== START PAGINATION ====== -->
    <ais-pagination
      :class-names="{
        'ais-Pagination-list':
          'flex items-center justify-center gap-1 sm:gap-2 mb-10 text-lg sm:text-xl',
        'ais-Pagination-item':
          'w-7 h-7 rounded-full text-harwood-red transition-colors duration-300 text-center content-center',
        'ais-Pagination-item--selected': 'bg-harwood-red text-white',
        'ais-Pagination-item--disabled': 'cursor-not-allowed opacity-60',
      }"
      :show-first="false"
      :show-last="false"
      @page-change="onPageChange"
    />
    <!-- ====== END PAGINATION ====== -->
  </ais-instant-search>
</template>
